<template>

  <div class="checkout-shipping-container row equal">

    <div class="leftside col-md-7 checkout-shipping">
      <div class="col-md-offset-4 col-md-8">

        <checkout-breadcrumb/>
        <!-- PAYMENT ADDRESS / SHIPPING ADDRESS -->

        <h5 class="billing-address"><i class="far fa-map-marker-alt"></i> {{ __t('shop.checkout.billing_address') }}</h5>
        <address-form
            :data="checkoutInfo.billingAddress"
            :errors="errors"
            @clear-errors="clearErrors"
            :countries="countries"
            type="billing"
            @address:change="handlePredictionChange($event,'billing')"
            @country:change="handleAddressChange($event,'billing')"
            @postal_code:blur="handleAddressChange($event,'billing')"
            @state:change="handleAddressChange($event,'billing')"

        />


        <div class="sameAddress-wrapper">
          <label class="size-12">
          <input v-model="checkoutInfo.sameAddress" class="checkbox-inline" type="checkbox"
                 @change="handleSameAddressChange">
            {{ __t('shop.checkout.billing_address_same_as_shipping') }}
          </label>
        </div>


        <!-- SHIPPING ADDRESS (in case it's different than the payment address) -->
        <div v-if="!checkoutInfo.sameAddress" class="shipping-address-wrapper">
        <h5 class="shipping-address"><i class="far fa-map-marker-alt"></i> {{ __t('shop.checkout.shipping_address') }}</h5>

          <address-form
              :data="checkoutInfo.shippingAddress"
              :errors="errors"
              @clear-errors="clearErrors"
              :countries="countries"
              type="shipping"
              @address:change="handlePredictionChange($event,'shipping')"
              @country:change="handleAddressChange($event,'shipping')"
              @postal_code:blur="handleAddressChange($event,'shipping')"
              @state:change="handleAddressChange($event,'shipping')"
          />

        </div>

        <div class="shipping-methods-wrapper">
          <h5 class="shipping-methods"><i class="far fa-truck"></i> {{ __t('shop.checkout.shipping_methods') }}</h5>
          <span class="text-danger" v-if="(!checkoutInfo.shipping_method_id && validationAttempted) || hasError('shippingMethodId')">
            {{ __t('shop.checkout.shipping_method_required') }}
          </span>
          <!-- SHIPPING METHODS -->
          <div :class="{'border-danger' : hasError('shippingMethodId')}">
            <div class="flex justify-center" v-if="shippingMethodsLoading">
              <span><i class="fa fa-spinner fa-spin"></i></span>
            </div>
            <div v-else>
              <div v-if="!checkoutInfo.billingAddress.google_place_id && !shippingMethods.length">
                {{ __t('shop.checkout.enter_address_for_shipping_methods') }}
              </div>

              <div v-for="shippingMethod in shippingMethods" :key="shippingMethod.id">

                <label class='shipping-method-label'>
                  <input type="radio" @change="handleShippingMethodChange(shippingMethod.id)"
                    v-model="checkoutInfo.shipping_method_id" :value="shippingMethod.id">
                  <span>
                    {{ shippingMethod.name }}
                  </span>
                  <span v-if="shippingMethod.delivery_estimation_time">, {{shippingMethod.delivery_estimation_time}} {{ __t('shop.checkout.estimated_delivery_time') }}</span>
                </label>

                <span v-if="shippingMethod.price > 0">
                    + {{ shippingMethod.price }} {{ currency }}
                </span>
                <span v-if="shippingMethod.company_integration && shippingMethod.company_integration.slug == 'box_now'">
                  <button class="btn is-btn-primary shop-button" @click="openBoxNowPopup(shippingMethod)">
                    {{ shippingLocation ? `Locker selected: ${shippingLocation.boxnowLockerId}` : 'Choose box' }}
                  </button>
                </span>
                <div v-if="shippingLocation">
                  <span>{{shippingLocation.boxnowLockerName}}, {{shippingLocation.boxnowLockerAddressLine1}}, {{shippingLocation.boxnowLockerAddressLine2}}</span>
                </div>
                <div
                  v-if="shippingMethod.customer_note"
                  class="shipping-method-customer-note"
                  v-text="shippingMethod.customer_note"
                ></div>
              </div>
            </div>
          </div>
        </div>

        <!-- PAYMENT METHODS -->
        <div class="payment-methods-wrapper">
            <h5 class="payment-methods">
              <i class="far fa-credit-card"></i> {{ __t('shop.checkout.payment_methods') }}
            </h5>
            <span class="text-danger" v-if="(!checkoutInfo.payment_method_id && validationAttempted) || hasError('paymentMethodId')">
              {{ __t('shop.checkout.payment_method_required') }}
            </span>

            <div :class="{ 'border-danger': hasError('paymentMethodId') }">
   
            <div v-if="checkoutInfo.shipping_method_id && availablePaymentMethods.length === 0">
              <p>{{ __t('shop.checkout.total_is_within_range_for_payment_methods') }}</p>
            </div>
        
            <div v-else>
                <div v-for="paymentMethod in availablePaymentMethods" :key="paymentMethod.id">              
                    <div>
                        <label class='payment-method-label'>
                            <input
                                type="radio"
                                :value="paymentMethod.id"
                                :checked="paymentMethod.id == checkoutInfo.payment_method_id"
                                @change="handlePaymentMethodChange(paymentMethod)"
                            >
                            <span>
                                {{ paymentMethod.name }}
                            </span>
                        </label>

                        <span v-if="paymentMethod.price > 0">
                            + {{ paymentMethod.price }} {{ currency }}
                        </span>

                        <div v-if="checkoutInfo.payment_method_id == paymentMethod.id && paymentMethod.instructions">
                            <pre>{{ paymentMethod.instructions }}</pre>
                        </div>

                        <span v-if="paymentMethod.images && paymentMethod.images.length" class="payment-method-icons">
                            <img v-for="(icon, idx) in paymentMethod.images" :key="icon + '-' + idx" :src="icon" class="payment-method-icon" alt="" />
                        </span>
                    </div>
                </div>
            </div>
            </div>
        </div>



        <div class="invoice-details-wrapper">
          <h5 class="document-type"><i class="far fa-receipt"></i> {{ __t('shop.checkout.document_type') }}</h5>
          <div class="text-left">
            <div>
            <label class="control-label" style="display:inline-block;">
              <input type="radio" v-model="checkoutInfo.request_invoice" value="0"
                     :checked="checkoutInfo.request_invoice == '0'">
              <span>
              {{ __t('shop.checkout.receipt') }}
              </span>
            </label>
            </div>
            <div>
            <label class="control-label">
              <input type="radio" v-model="checkoutInfo.request_invoice" value="1"
                     :checked="checkoutInfo.request_invoice == '1'">
              <span>
              {{ __t('shop.checkout.invoice') }}
              </span>
            </label>
            </div>
          </div>



        </div>

        <div v-if="checkoutInfo.request_invoice == '1'">


          <!-- COMPANY NAME -->
          <span  class="text-danger" v-if="hasError('companyCustomer.title')">
            {{errors['companyCustomer.title'][0]}}
          </span>

          <div class="form-group">
            <label class="control-label"><span class="sr-only">{{__t('shop.checkout.company_title')}}</span>
              <input
                  type="text"
                  class="form-control"
                  :placeholder="__t('shop.checkout.company_title')"
                  v-model="checkoutInfo.companyCustomer.title"
                  :class="{error: hasError('companyCustomer.title')}"
                  @keyup="clearErrors"
              >
            </label>
          </div>

          <!-- BUSINESS TYPE -->
          <span  class="text-danger" v-if="hasError('companyCustomer.business_type')">
            {{errors['companyCustomer.business_type'][0]}}
          </span>
          <div class="form-group">
            <label class="control-label"><span class="sr-only">{{__t('shop.checkout.business_type')}}</span>
            <input
              type="text"
              class="form-control"
              v-model="checkoutInfo.companyCustomer.business_type"
              :placeholder="__t('shop.checkout.business_type')"
              :class="{error: hasError('companyCustomer.business_type')}"
              @keyup="clearErrors"
            >
            </label>
          </div>

          <!-- VAT NUMBER -->
          <span  class="text-danger" v-if="hasError('companyCustomer.vat_number')">
            {{errors['companyCustomer.vat_number'][0]}}
          </span>
          <div class="form-group">
            <label class="control-label"><span class="sr-only">{{__t('shop.checkout.vat_number')}}</span>
            <input
              type="text"
              class="form-control"
              v-model="checkoutInfo.companyCustomer.vat_number"
              :placeholder="__t('shop.checkout.vat_number')"
              :class="{error: hasError('companyCustomer.vat_number')}"
              @keyup="clearErrors"
            >
            </label>
          </div>

          <!-- TAX OFFICE -->
          <span  class="text-danger" v-if="hasError('companyCustomer.tax_office')">
            {{errors['companyCustomer.tax_office'][0]}}
          </span>
          <div class="form-group">
            <label class="control-label"><span class="sr-only">{{__t('shop.checkout.tax_office')}}</span>
            <input
              type="text"
              class="form-control"
              v-model="checkoutInfo.companyCustomer.tax_office"
              :placeholder="__t('shop.checkout.tax_office')"
              :class="{error: hasError('companyCustomer.tax_office')}"
              @keyup="clearErrors"
            >
            </label>
          </div>

          <!-- Email -->
          <span class="text-danger" v-if="hasError('companyCustomer.email')">
            {{ errors['companyCustomer.email'][0] }}
          </span>
          <div class="form-group">
            <label class="control-label"><span class="sr-only">Email</span>
            <input
              type="text"
              class="form-control"
              placeholder="Email"
              v-model="checkoutInfo.companyCustomer.email"
              :class="{error: hasError('companyCustomer.email')}"
              @keyup="clearErrors"
            >
            </label>
          </div>

          <!-- PHONE -->
          <span  class="text-danger" v-if="hasError('companyCustomer.mobile')">
            {{errors['companyCustomer.mobile'][0]}}
          </span>
          <div class="form-group">
            <label class="control-label"><span class="sr-only">{{__t('shop.checkout.company_phone')}}</span>
            <input
              type="text"
              class="form-control"
              :placeholder="__t('shop.checkout.company_phone')"
              v-model="checkoutInfo.companyCustomer.mobile"
              :class="{error: hasError('companyCustomer.mobile')}"
              @keyup="clearErrors"
            >
            </label>
          </div>

        </div>

        <div class="hidden-xs">
          <action-buttons @submit="submit"/>
        </div>
      </div>
    </div>
    <div class="rightside col-md-5">
      <div class="cart-container col-md-8">

        <div style="margin-bottom: 32px;display: flex;justify-content:flex-start;align-items: center">
          <i style="color:#1ABA75" class="fal fa-6x fa-bags-shopping"></i>
          <p style="margin-left: 24px">{{ __t('shop.cart.your_cart_has_x_products',{x: $_.sumBy(cart.items,(i) => Number(i.quantity)) }) }}</p>
        </div>

        <cart-table :cart="cart" :total-items-cost="cart.cart_after_discount_cost"
          :total-discount="cart.cart_cost - cart.cart_after_discount_cost" :default-vat="defaultVat"
          :b2b-enabled="b2bEnabled" @vat-total="updateVatTotal" />


        <div class="text-right">
          <div v-if="selectedPaymentMethod && paymentMethodCost">
            {{ selectedPaymentMethod.name }}: {{ selectedPaymentMethod.price }} {{ currency }}
          </div>
          <div v-if="selectedShippingMethod && shippingCost">
            {{ selectedShippingMethod.name }}: {{ selectedShippingMethod.price }} {{ currency }}
          </div>
          <div >
            <hr class="col-md-6 col-md-offset-5 " style="margin-top:30px;margin-bottom:5px" />
            <div class="cart-order-total">{{ __('webapp.order_total') }}: {{ totalCost}} {{ currency }}</div>
            <div  class="cart-vat-included" > {{ __('webapp.taxes_included') }} </div>
          </div>


        </div>
        <div class="form-group">
          <label for="checkout-shipping-order-comments-input" class="control-label">{{ __('companyadmin.comments') }}</label>
          <textarea v-model="checkoutInfo.orderComments" name="" id="checkout-shipping-order-comments-input"  style="resize:vertical" class="form-control"></textarea>
        </div>


        <div class="visible-xs">
          <action-buttons @submit="submit" />
        </div>
      </div>
    </div>

    <div id="boxnow-popup" class="modal fade" tabindex="-1" role="dialog">
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <button
            type="button"
            class="boxnow-modal-close"
            aria-label="Close"
            @click="closeBoxNowPopup"
          >
            ×
          </button>
          <div class="modal-body">
            <div id="boxnowmap" style="height:90vh;"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>


import {mapActions} from 'vuex';
import {mapGetters} from 'vuex';
import ResizableTextarea from '@/components/ui/resizable-textarea';

import ActionButtons from './eshop-checkout-shipping-action-buttons';
import checkoutBreadcrumb from './eshop-checkout-breadcumb';

import AddressForm from './eshop-address-form'
import CartTable from './common/cart-table'

export default {
  components: {AddressForm, ResizableTextarea, CartTable, checkoutBreadcrumb, ActionButtons},
  data: function () {
    return {
      currency: window.currency,
      alias: window.company_alias,
      errors: [],
      countries: [],
      shippingMethodsLoading: false,
      shippingMethods: [],
      vatTotal: 0,
      shippingLocation: null,
      validationAttempted: false,
      boxNowMessageHandler: null,
    }
  },
  props: {
    defaultVat: {
      type: Object,
      default: null
    },
    b2bEnabled: {
      type: String,
      required: true
    },
  },
  computed: {
    ...mapGetters({cart: 'getCart'}),
    ...mapGetters({shopInfo: 'getShopInfo'}),
    ...mapGetters({checkoutInfo: 'getCheckoutInfo'}),
    totalCost() {
      var cost = Number(this.cart.cart_after_discount_cost);

      if(this.selectedShippingMethod){
        cost += Number(this.selectedShippingMethod.price);
      }

      if(this.selectedPaymentMethod){
        cost += Number(this.selectedPaymentMethod.price);
      }
      if (!this.b2bEnabled) {
        cost += parseFloat(this.vatTotal);
      }
      return cost.toFixed(2);

    },
    cartCost(){
      return this.cart.cart_cost;
    },
    shippingCost() {
      if (!this.selectedShippingMethod) return 0;
      return Number(this.selectedShippingMethod.price);
    },
    paymentMethodCost() {
      if (!this.selectedPaymentMethod) return 0;
      return Number(this.selectedPaymentMethod.price);
    },
    paymentMethods() {
      if (!this.shopInfo) return [];
      return this.shopInfo.paymentMethods;
    },
    availablePaymentMethods() {
      return this.paymentMethods.filter(paymentMethod => {
        const minPurchaseTotal = parseFloat(paymentMethod.minimumPurchaseTotal);
        const totalCost = parseFloat(this.totalCost);

        if (paymentMethod.enableMaxValue) {
          const withinTotal =
            totalCost >= minPurchaseTotal &&
            totalCost <= parseFloat(paymentMethod.maximumPurchaseTotal);
          if (!withinTotal) return false;
        } else {
          if (totalCost < minPurchaseTotal) return false;
        }

        if (paymentMethod.restricted_to) {
          let allowedIds = [];

          try {
            allowedIds = Array.isArray(paymentMethod.restricted_to)
              ? paymentMethod.restricted_to.map(String)
              : JSON.parse(paymentMethod.restricted_to).map(String);
          } catch (e) {
            // stored as CSV fallback
            allowedIds = String(paymentMethod.restricted_to)
              .split(',')
              .map(s => s.trim());
          }

          const currentShipping = String(this.checkoutInfo.shipping_method_id);
          if (currentShipping && !allowedIds.includes(currentShipping)) {
            return false;
          }
        }

        return true;
      });
    },
    selectedPaymentMethod() {
      return this.paymentMethods.find(x => x.id == this.checkoutInfo.payment_method_id);
    },
    selectedPaymentMethodType() {
      return this.selectedPaymentMethod ? this.selectedPaymentMethod.type : 'offline';
    },
    selectedShippingMethod() {
      return this.shippingMethods.find(x => x.id == this.checkoutInfo.shipping_method_id);
    },
    hasErrors() {
      return !this.$_.isEmpty(this.errors)
    },
    canSubmit() {
      return !!(this.checkoutInfo.shipping_method_id && this.checkoutInfo.payment_method_id);
    }
  },
  watch: {
    'checkoutInfo.shipping_method_id'(newVal) {
      this.clearErrors();
      this.validationAttempted = false;

      if (!newVal) {
        this.checkoutInfo.payment_method_id = null;
        return;
      }
      const allowedIds = this.availablePaymentMethods.map(m => String(m.id));
      if (!allowedIds.includes(String(this.checkoutInfo.payment_method_id))) {
        this.checkoutInfo.payment_method_id = null;
      }
    },
    'checkoutInfo.payment_method_id'(newVal) {
      this.validationAttempted = false;
    }
  },
   methods: {
    ...mapActions([
      'initCart',
      'fetchShopInfo',
      'fetchCheckoutInfo',
      'fetchPaymentMethods'
    ]),
    hasError(field) {
      return this.hasErrors && this.errors.hasOwnProperty(field);
    },
    clearErrors() {
      this.errors = []
    },
    requiredMessage(label) {
      const message = __('validation.required');

      if (message && message !== 'validation.required') {
        return message.replace(':attribute', label);
      }

      return `The ${label} field is required.`;
    },
    fieldValue(path) {
      return path.split('.').reduce((data, key) => {
        return data && data[key] !== undefined ? data[key] : null;
      }, this.checkoutInfo);
    },
    addRequiredError(errors, field, label, valuePath = field) {
      const value = this.fieldValue(valuePath);

      if (value === null || value === undefined || value === '') {
        errors[field] = [this.requiredMessage(label)];
      }
    },
    requiredFieldErrors() {
      const errors = {};
      const billingAddress = this.__t('shop.checkout.billing_address');
      const shippingAddress = this.__t('shop.checkout.shipping_address');

      this.addRequiredError(errors, 'billingAddress.address', `${billingAddress} - ${this.__t('shop.checkout.address')}`);
      this.addRequiredError(errors, 'billingAddress.country_code', `${billingAddress} - ${this.__t('shop.checkout.country')}`);
      this.addRequiredError(errors, 'billingAddress.city', `${billingAddress} - ${this.__t('shop.checkout.city')}`);
      this.addRequiredError(errors, 'billingAddress.postal_code', `${billingAddress} - ${this.__t('shop.checkout.postal_code')}`);

      if (!this.checkoutInfo.sameAddress) {
        this.addRequiredError(errors, 'shippingAddress.address', `${shippingAddress} - ${this.__t('shop.checkout.address')}`);
        this.addRequiredError(errors, 'shippingAddress.country_code', `${shippingAddress} - ${this.__t('shop.checkout.country')}`);
        this.addRequiredError(errors, 'shippingAddress.city', `${shippingAddress} - ${this.__t('shop.checkout.city')}`);
        this.addRequiredError(errors, 'shippingAddress.postal_code', `${shippingAddress} - ${this.__t('shop.checkout.postal_code')}`);
      }

      this.addRequiredError(errors, 'shippingMethodId', this.__t('shop.checkout.shipping_methods'), 'shipping_method_id');
      this.addRequiredError(errors, 'paymentMethodId', this.__t('shop.checkout.payment_methods'), 'payment_method_id');

      if (this.checkoutInfo.request_invoice == '1') {
        this.addRequiredError(errors, 'companyCustomer.title', this.__t('shop.checkout.company_title'));
        this.addRequiredError(errors, 'companyCustomer.business_type', this.__t('shop.checkout.business_type'));
        this.addRequiredError(errors, 'companyCustomer.vat_number', this.__t('shop.checkout.vat_number'));
        this.addRequiredError(errors, 'companyCustomer.tax_office', this.__t('shop.checkout.tax_office'));
      }

      return errors;
    },
    showValidationErrors(errors) {
      this.$_.forEach(errors, messages => {
        this.$_.forEach(messages, message => {
          toastr.error(message);
        });
      });
    },
    handleShippingMethodChange(shippingMethodId){
      this.shippingLocation = null;

      if(!shippingMethodId){
        this.checkoutInfo.payment_method_id = null;
        return;
      }

      this.fetchPaymentMethods({
        alias: this.alias,shippingMethodId
      }).then(methods => {
        if(!methods.find(x => x.id == this.checkoutInfo.payment_method_id)){
          this.checkoutInfo.payment_method_id = null;
        }
        if(methods.length === 1){
          this.checkoutInfo.payment_method_id = methods[0].id;
        }
      });
    },
    handlePaymentMethodChange(paymentMethod) {
      this.checkoutInfo.payment_method_id = paymentMethod.id;
    },
    handleSameAddressChange(event) {
      this.clearErrors();
      this.checkoutInfo.shipping_method_id = null;
      this.shippingLocation = null;

      // reload shipping methods if same address checkbox
      // changed
      if (this.checkoutInfo.sameAddress && this.checkoutInfo.billingAddress.google_place_id) {
        this.checkoutInfo.shipping_method_id = null;
        this.loadShippingMethods(this.checkoutInfo.billingAddress);

      }

      if (!this.checkoutInfo.sameAddress && this.checkoutInfo.shippingAddress.google_place_id) {
        this.checkoutInfo.shipping_method_id = null;
        this.loadShippingMethods(this.checkoutInfo.shippingAddress);
      }
    },
    predictionAutoComplete(addressData, prediction) {

      addressData.lat = prediction.geometry.location.lat();
      addressData.lng = prediction.geometry.location.lng();
      addressData.google_place_id = prediction.place_id;

      var postalCodeComponent = prediction.address_components.find(component => component.types.includes('postal_code'));
      if (postalCodeComponent) addressData.postal_code = postalCodeComponent.short_name.replace(/ /g, '');


      var cityComponent = prediction.address_components.find(component => component.types.includes('locality'));
      if (cityComponent) addressData.city = cityComponent.short_name

      var countryComponent = prediction.address_components.find(component => component.types.includes('country'));
      if (countryComponent) {
        var country = this.countries.find(x => x.code == countryComponent.short_name);
        if (country) {
          addressData.country_code = country.code3;
        }
      }
    },
    loadShippingMethods(address) {

      if (!address.lat || !address.lng) return;

      this.shippingMethodsLoading = true;
      var query = {
        alias: this.alias,
        _query: {
          address,
          locale: Lang.locale,
        }
      }

      $.getJSON(route('webapp.shop.shipping-methods', query).toString(), (response) => {
        if (response.status == 'ok') {
          this.shippingMethods = response.shippingMethods
        }

        if (response.status == 'error') {
          toastr.error(response.message);
          this.shippingMethods = [];
        }

      }).always(() => {
        this.shippingMethodsLoading = false;
      })


    },
    handleAddressChange(data, type) {
      if (type == 'billing' && !this.checkoutInfo.sameAddress) return;
      this.shippingLocation = null;
      var address = type == 'billing' ?
          this.checkoutInfo.billingAddress :
          this.checkoutInfo.shippingAddress;

      this.loadShippingMethods(address);
    },
    handlePredictionChange(prediction, type) {

      if (type == 'billing') this.predictionAutoComplete(this.checkoutInfo.billingAddress, prediction);
      if (type == 'shipping') this.predictionAutoComplete(this.checkoutInfo.shippingAddress, prediction);

      // if billing changes but shipping address is set
      // different return
      if (type == 'billing' && !this.checkoutInfo.sameAddress) return;

      this.checkoutInfo.shipping_method_id = null;
      this.shippingLocation = null;

      var address = type == 'billing' ?
          this.checkoutInfo.billingAddress :
          this.checkoutInfo.shippingAddress;

      this.loadShippingMethods(address);

    },
    openBoxNowPopup(shippingMethod) {
      if (!shippingMethod || !shippingMethod.company_integration) {
        console.error("Shipping method is not set.");
        return;
      }

      this.renderBoxNowWidget(shippingMethod);

      // Open Bootstrap modal using jQuery (assuming Bootstrap is loaded globally)
      $("#boxnow-popup").modal("show");
    },
    closeBoxNowPopup() {
      $("#boxnow-popup").modal("hide");
      this.clearBoxNowWidget();
    },
    renderBoxNowWidget(shippingMethod) {
      const mapElement = document.getElementById("boxnowmap");
      if (!mapElement) return;

      this.clearBoxNowWidget();

      const address = this.boxNowShippingAddress();
      const country = this.boxNowCountry(address);
      const iframe = document.createElement("iframe");

      iframe.src = this.boxNowWidgetUrl(shippingMethod, country, address);
      iframe.setAttribute("allowtransparency", "true");
      iframe.setAttribute("allow", "geolocation");
      iframe.setAttribute("style", "border:0;width:100%;height:100%;");

      this.boxNowMessageHandler = (event) => {
        if (!this.isAllowedBoxNowOrigin(event.origin)) return;
        if (!event.data) return;

        this.submitBoxLocation(event.data);
      };

      window.addEventListener("message", this.boxNowMessageHandler);
      mapElement.appendChild(iframe);
    },
    clearBoxNowWidget() {
      if (this.boxNowMessageHandler) {
        window.removeEventListener("message", this.boxNowMessageHandler);
        this.boxNowMessageHandler = null;
      }

      const mapElement = document.getElementById("boxnowmap");
      if (mapElement) {
        mapElement.innerHTML = "";
      }
    },
    boxNowShippingAddress() {
      return this.checkoutInfo.sameAddress
        ? this.checkoutInfo.billingAddress
        : this.checkoutInfo.shippingAddress;
    },
    boxNowCountry(address) {
      const countryCode = String(address.country_code || "").toUpperCase();

      if (countryCode === "CYP" || countryCode === "CY") return "cy";

      return "gr";
    },
    boxNowWidgetUrl(shippingMethod, country, address) {
      const widgetDomain = country === "cy"
        ? "https://widget-v5.boxnow.cy"
        : "https://widget-v5.boxnow.gr";

      const params = new URLSearchParams({
        partnerId: shippingMethod.company_integration.pivot.client_id,
        gps: "no",
        autoselect: "yes",
        autoclose: "no",
      });

      if (address.postal_code) {
        params.set("zip", address.postal_code);
      }

      if (country === "cy") {
        params.set("countryCode", "cy");
      }

      return `${widgetDomain}/iframe.html?${params.toString()}`;
    },
    isAllowedBoxNowOrigin(origin) {
      return [
        "https://widget-v5.boxnow.gr",
        "https://widget-v5.boxnow.cy",
      ].includes(origin);
    },
    submitBoxLocation(selected) {
      this.shippingLocation = selected;
    },
    updateVatTotal(vatTotal) {
      this.vatTotal = vatTotal;
    },
    submit() {
      this.validationAttempted = true;
      const errors = this.requiredFieldErrors();

      if (!this.$_.isEmpty(errors)) {
        this.errors = errors;
        this.showValidationErrors(errors);
        return;
      }

      var self = this;
      var data = {
        locale: window.language,
        sameAddress: this.checkoutInfo.sameAddress ? 1 : 0,
        billingAddress: this.checkoutInfo.billingAddress,
        shippingAddress: this.checkoutInfo.shippingAddress,
        paymentMethodId: this.checkoutInfo.payment_method_id,
        shippingMethodId: this.checkoutInfo.shipping_method_id,
        request_invoice: this.checkoutInfo.request_invoice,
        companyCustomer: this.checkoutInfo.companyCustomer,
        orderComments: this.checkoutInfo.orderComments,
        shippingLocation: JSON.stringify(this.shippingLocation),
      };

      $.ajax({
        type: 'POST',
        url: route('webapp.ordering.checkout.submit.shipping', this.alias),
        data: data,
        success(response) {

          if (response.hasOwnProperty('redirect')) {
            // self.$emit('order:secure-step',response.orderId);
            window.location.href = response.redirect;
          }

        },
        error(error) {
          self.errors = error.responseJSON.errors || {};
          self.showValidationErrors(self.errors);
        }
      });

    }
  },
  mounted() {
    // $('body').scroll(function(){
    //     $('.cart-container').css('top', $(this).scrollTop());
    // });
  },
  created() {
    $.getJSON(route('webapp.countries.countries', this.alias), (response) => {
      this.countries = response.data;
      this.fetchCheckoutInfo().then(checkoutInfo => {
        this.loadShippingMethods(checkoutInfo.sameAddress ? checkoutInfo.billingAddress : checkoutInfo.shippingAddress);

        this.handleShippingMethodChange(this.checkoutInfo.shipping_method_id);
      });
    })

    this.initCart(this.alias);
    this.fetchShopInfo();
  },
  beforeDestroy() {
    this.clearBoxNowWidget();
  }
}
</script>

<style scoped>
.checkout-shipping-container h5{
  margin-top: 15px;
}
.shipping-method {
  margin: 12px;
  padding: 4px 8px;
  background-color: #ccc;
}

.shipping-method a {
  color: #888;
}

.shipping-method.selected {
  background-color: #6bc98a;
}

.shipping-method.selected a {
  color: white;
}

.shipping-method-customer-note {
  color: #666;
  font-size: 13px;
  line-height: 1.4;
  margin: 4px 0 10px 22px;
  white-space: pre-line;
}

.table > tbody > tr > td {
  vertical-align: middle;
  font-size: 16px;
}


.payment-method-label,
.shipping-method-label {
  font-size: 16px;
  display: inline-block;
  font-weight: normal;
}

.payment-method-icons {
  display: inline-flex;
  gap: 6px;
  margin-right: 8px;
  vertical-align: middle;
}

.payment-method-icon {
  width: 40px;
  height: 30px;
  object-fit: contain;
  display: inline-block;
}
.border-danger {
  border: 1px solid #a94442;
  border-radius: 4px;
}

.checkout-shipping h5:not(.billing-address) {
  margin-top:30px;
}

.modal-content {
  position: relative;
}

.boxnow-modal-close {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.95);
  color: #333;
  font-size: 28px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

</style>
